import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Heart, ThumbsDown, ThumbsUp, Undo2 } from "lucide-react";

import {
  useApplicants,
  useConfirmWorker,
  useSetApplicantInterest,
} from "@/features/applications/model/useApplications";
import type {
  Applicant,
  ApplicationStatus,
  ShortlistStatus,
} from "@/features/applications/types";
import { APPLICATION_STATUS_LABELS } from "@/features/applications/types";
import { useShift } from "@/features/shifts";
import { cn } from "@/shared/lib/cn";
import { BackLink, Badge } from "@/shared/ui";

const TABS: { key: ApplicationStatus | "ALL"; label: string }[] = [
  { key: "ALL", label: "All" },
  { key: "PENDING", label: "Pending" },
  { key: "INTERESTED", label: "Interested" },
  { key: "PASSED", label: "Passed" },
  { key: "CONFIRMED", label: "Confirmed" },
];

function StarRow({ avg }: { avg: number | null }) {
  if (avg == null)
    return <span className="text-xs text-muted-foreground">No ratings yet</span>;
  return <span className="text-xs text-muted-foreground">★ {avg.toFixed(1)} avg</span>;
}

const STATUS_BADGE: Record<ApplicationStatus, string> = {
  PENDING: "bg-muted text-muted-foreground",
  INTERESTED: "bg-success-light text-success-foreground",
  PASSED: "bg-warning-light text-warning-foreground",
  CONFIRMED: "bg-primary text-primary-foreground",
  WITHDRAWN: "bg-muted text-muted-foreground",
};

export function ApplicantList() {
  const { id: shiftId } = useParams<{ id: string }>();
  const { data: shift } = useShift(shiftId);
  const { data: applicants, isLoading, error } = useApplicants(shiftId);
  const confirm = useConfirmWorker();
  const setInterest = useSetApplicantInterest();
  const [activeTab, setActiveTab] = useState<ApplicationStatus | "ALL">("ALL");
  // Track in-flight mutations per applicant so loading state stays scoped to
  // the clicked row instead of every row sharing the mutation hook.
  const [pendingConfirmIds, setPendingConfirmIds] = useState<Set<string>>(new Set());
  const [pendingInterestIds, setPendingInterestIds] = useState<Set<string>>(new Set());

  const shiftIsOpen = shift?.status === "OPEN";

  const counts = useMemo(() => {
    const map: Record<string, number> = { ALL: applicants?.length ?? 0 };
    applicants?.forEach((a) => {
      map[a.status] = (map[a.status] ?? 0) + 1;
    });
    return map;
  }, [applicants]);

  const filtered = useMemo(() => {
    if (!applicants) return [];
    if (activeTab === "ALL") return applicants;
    return applicants.filter((a) => a.status === activeTab);
  }, [applicants, activeTab]);

  const backTo = shiftId ? `/venue/shifts/${shiftId}` : "/venue";

  if (isLoading) {
    return (
      <div className="space-y-4">
        <BackLink to={backTo} label="Back to shift" />
        <div className="text-sm text-muted-foreground">Loading applicants…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-4">
        <BackLink to={backTo} label="Back to shift" />
        <div className="text-sm text-destructive">Could not load applicants.</div>
      </div>
    );
  }

  const onSetInterest = (a: Applicant, interest: ShortlistStatus) => {
    setPendingInterestIds((s) => new Set(s).add(a.application_id));
    setInterest.mutate(
      {
        shiftId: shiftId!,
        applicationId: a.application_id,
        interest,
      },
      {
        onSettled: () =>
          setPendingInterestIds((s) => {
            const next = new Set(s);
            next.delete(a.application_id);
            return next;
          }),
      },
    );
  };

  const onConfirm = (applicationId: string) => {
    setPendingConfirmIds((s) => new Set(s).add(applicationId));
    confirm.mutate(
      { shiftId: shiftId!, applicationId },
      {
        onSettled: () =>
          setPendingConfirmIds((s) => {
            const next = new Set(s);
            next.delete(applicationId);
            return next;
          }),
      },
    );
  };

  const requiredCerts = shift?.required_certifications ?? [];
  const computeCertMatch = (a: Applicant): {
    allPresent: boolean;
    missing: string[];
  } => {
    // Match by category (exact equality, allow-list-backed). Fall back to
    // the free-text name for legacy rows that pre-date the category field
    // so we don't flag those workers as "missing" during the transition.
    const owned = new Set<string>();
    a.certifications.forEach((c) => {
      if (c.category) owned.add(c.category.trim().toLowerCase());
      else owned.add(c.name.trim().toLowerCase());
    });
    const missing = requiredCerts.filter(
      (req) => !owned.has(req.trim().toLowerCase()),
    );
    return { allPresent: missing.length === 0, missing };
  };

  return (
    <div className="space-y-4">
      <BackLink to={backTo} label="Back to shift" />
      <div>
        <h2 className="text-lg font-semibold tracking-display-tight text-copy">Applicants</h2>
        {!shiftIsOpen && (
          <p className="text-sm text-copy-muted">
            This shift is {shift?.status.toLowerCase()}.
          </p>
        )}
        {shiftIsOpen && (
          <p className="text-sm text-copy-muted">
            Mark applicants <strong>Interested</strong> or <strong>Pass</strong> to sort them
            without a final decision. Confirm one when you're sure — the rest are marked Passed
            automatically.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((tab) => {
          const count = counts[tab.key] ?? 0;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "rounded-t-md border-b-2 px-3 py-2 text-xs font-medium transition-colors",
                isActive
                  ? "border-primary text-copy"
                  : "border-transparent text-copy-muted hover:bg-surface-sunken",
              )}
            >
              {tab.label}{" "}
              <span className="text-copy-subtle">({count})</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface p-8 text-center text-sm text-copy-muted">
          {applicants && applicants.length === 0
            ? "No applicants yet."
            : "No applicants in this bucket."}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((a) => (
            <div
              key={a.application_id}
              className="rounded-lg border border-border bg-surface p-4 shadow-card"
            >
              <div className="flex items-start gap-4">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full border border-border bg-surface-sunken">
                  {a.photo_url ? (
                    <img
                      src={a.photo_url}
                      alt={a.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs text-copy-subtle">
                      No photo
                    </div>
                  )}
                </div>

                <div className="flex-1 space-y-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-copy">
                      {a.name || "(name pending)"}
                    </h3>
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-xs font-medium",
                        STATUS_BADGE[a.status],
                      )}
                    >
                      {APPLICATION_STATUS_LABELS[a.status]}
                    </span>
                    {requiredCerts.length > 0 &&
                      (() => {
                        const match = computeCertMatch(a);
                        if (match.allPresent) {
                          return <Badge variant="success">All certs ✓</Badge>;
                        }
                        const shown = match.missing.slice(0, 2).join(", ");
                        const overflow = match.missing.length > 2 ? "..." : "";
                        return (
                          <Badge
                            variant="warning"
                            title={`Missing: ${match.missing.join(", ")}`}
                          >
                            <span className="overflow-hidden">
                              Missing: {shown}
                              {overflow}
                            </span>
                          </Badge>
                        );
                      })()}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-copy-muted">
                    {a.city && <span>{a.city}</span>}
                    <StarRow avg={a.rating_avg} />
                    <span>{a.shift_count} shifts completed</span>
                  </div>
                  {a.certifications.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1 text-xs">
                      {a.certifications.map((cert) => (
                        <a
                          key={cert.id}
                          href={cert.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline"
                          title={
                            cert.expires_on
                              ? `Expires ${cert.expires_on}`
                              : "No expiry on file"
                          }
                        >
                          {cert.category ?? cert.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 flex-col items-end gap-2">
                  {shiftIsOpen &&
                    (a.status === "PENDING" || a.status === "PASSED") && (
                      <button
                        type="button"
                        onClick={() => onSetInterest(a, "INTERESTED")}
                        disabled={pendingInterestIds.has(a.application_id)}
                        className="inline-flex items-center gap-1 rounded-md border border-success bg-surface px-2.5 py-1.5 text-xs font-medium text-success-foreground hover:bg-success-light disabled:opacity-50"
                      >
                        <ThumbsUp className="h-3.5 w-3.5" /> Interested
                      </button>
                    )}
                  {shiftIsOpen &&
                    (a.status === "PENDING" || a.status === "INTERESTED") && (
                      <button
                        type="button"
                        onClick={() => onSetInterest(a, "PASSED")}
                        disabled={pendingInterestIds.has(a.application_id)}
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-copy-muted hover:bg-surface-sunken disabled:opacity-50"
                      >
                        <ThumbsDown className="h-3.5 w-3.5" /> Pass
                      </button>
                    )}
                  {shiftIsOpen &&
                    (a.status === "INTERESTED" || a.status === "PASSED") && (
                      <button
                        type="button"
                        onClick={() => onSetInterest(a, "PENDING")}
                        disabled={pendingInterestIds.has(a.application_id)}
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs text-copy-muted hover:bg-surface-sunken disabled:opacity-50"
                      >
                        <Undo2 className="h-3.5 w-3.5" /> Move back
                      </button>
                    )}
                  {shiftIsOpen &&
                    (a.status === "PENDING" ||
                      a.status === "INTERESTED" ||
                      a.status === "PASSED") && (
                      <button
                        type="button"
                        onClick={() => onConfirm(a.application_id)}
                        disabled={pendingConfirmIds.has(a.application_id)}
                        className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {pendingConfirmIds.has(a.application_id) ? "Confirming…" : "Confirm"}
                      </button>
                    )}
                  {a.status === "CONFIRMED" && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-primary-light px-2.5 py-1.5 text-xs font-medium text-primary">
                      <Heart className="h-3.5 w-3.5" /> Hired
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
